import type { JSX, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IReceiptSettings } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';

interface IEmailConfiguratorContext {
  editorConfig: IReceiptSettings | null;
  editorIsDirty: boolean;
  initialConfig: IReceiptSettings;
  setEditorConfig: (config: IReceiptSettings, isDirty: boolean) => void;
  handleSave: () => Promise<void>;
  isDirty: boolean;
  lastSaved: string | undefined;
}

const EmailConfiguratorContext = createContext<IEmailConfiguratorContext | undefined>(undefined);

interface IProviderProps {
  children: ReactNode;
}

export function EmailConfiguratorProvider({ children }: Readonly<IProviderProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();

  const initialConfig = account.get('receipt_settings') ?? {};

  const [editorConfigState, setEditorConfigState] = useState<IReceiptSettings | null>(null);
  const [editorIsDirty, setEditorIsDirty] = useState(false);

  const lastSaved = account.get('date_last_modified');

  const setEditorConfig = useCallback(
    (config: IReceiptSettings, isDirtyFromEditor: boolean): void => {
      setEditorConfigState(config);
      setEditorIsDirty(isDirtyFromEditor);
    },
    [],
  );

  const isDirty = editorIsDirty;

  const handleSave = useCallback(async (): Promise<void> => {
    if (editorConfigState) {
      try {
        account.set({
          receipt_settings: {
            ...editorConfigState,
            setup_completed: true,
          },
        });
        await account.patchDirty();
        setEditorIsDirty(false);
        toast(t('Changes saved.'), { type: 'success' });
      } catch (error) {
        showError(error, t);
      }
    }
  }, [account, editorConfigState, t]);

  const value = useMemo(
    () => ({
      editorConfig: editorConfigState,
      editorIsDirty,
      initialConfig,
      setEditorConfig,
      handleSave,
      isDirty,
      lastSaved,
    }),
    [
      editorConfigState,
      editorIsDirty,
      initialConfig,
      setEditorConfig,
      handleSave,
      isDirty,
      lastSaved,
    ],
  );

  return (
    <EmailConfiguratorContext.Provider value={value}>{children}</EmailConfiguratorContext.Provider>
  );
}

export function useEmailConfigurator(): IEmailConfiguratorContext {
  const context = useContext(EmailConfiguratorContext);
  if (context === undefined) {
    throw new Error('useEmailConfigurator must be used within EmailConfiguratorProvider');
  }
  return context;
}
